import { useCallback, createContext, useContext, useRef, ReactNode, useSyncExternalStore } from "react";
import { PetDataType } from "./sharedContextTypes/petTypes";
import { apiPOST } from "../utils/apiRequests";

//---------------<Data types>------------------------------------------------------------------------------------------

export type DigitalGeniusChatDataType = {
	countryCode?: "JE" |"IM" | "GG" | "GB" | "NL" | "BE" | "IE";
	petNames?: string;
	petType?: string;
	petFood?: string;
	firstDispatchDate?: string;
	bbFirstDispatchDate?: number;
	lastDispatchDate?: string;
	bbLastDispatchDate?: number;
	nextDelivery?: string;
	bbNextDelivery?: number;
	numberOfOrders?: number;
	boxSize?: string;
	amount?: string;
	allPetNames?: string;
	numberOfPets?: number;
	deliveryOut?: boolean;
	deliveryPaymentTaken?: boolean;
	eligibleForCredit?: boolean;
	lastCreditGiven?: string | null; 	// is this a string? 
	pets?: PetDataType[];
	btCustomer?: boolean;
	subscriptionID?: number;
	subscriptionStatusCancelled?: boolean;

	updateDigitalGeniusChatData: boolean;
	disableDigitalGeniusChat?: boolean;
}

type DigitalGeniusChatDataCallReturnType = {
	status?: boolean;
	data?: DigitalGeniusChatDataType;
	errors?: string[];
};

//---------------</Data types>-----------------------------------------------------------------------------------------
//---------------<Digital genius chat data context>--------------------------------------------------------------------

function useDigitalGeniusChatDataStore(): {
	get: () => DigitalGeniusChatDataType;
	set: (value: Partial<DigitalGeniusChatDataType>) => void; // so we can set individual properties of the object
	subscribe: (callback: () => void) => () => void;
} {
	// the init value is set inside, because the auth context should only initialize 1x
	// this adds to simplicity of use and there is no benefit to exposing it as a property
	const store = useRef<DigitalGeniusChatDataType>({
		updateDigitalGeniusChatData: true
	});

	// we use set, because we only want a single type of a function being added once
	const subscribers = useRef(new Set<()=> void>()); 

	const get = useCallback(() => store.current, []);
	const set = useCallback((value: Partial<DigitalGeniusChatDataType>) => {
		store.current = { ...store.current, ...value};
		subscribers.current.forEach((callback) => callback());
	} ,[]);

	// subscriber is added so we can subscribe to any value changes to the state (in our case of the "user") with useSyncExternalStore
	// when we add a subscriber we return the delete function of that subscriber
	const subscribe = useCallback((callback: () => void) => {
		subscribers.current.add(callback);
		return () => subscribers.current.delete(callback);
	} ,[]);

	return { get, set, subscribe };
}

type UseDigitalGeniusChatDataStoreReturnType = ReturnType<typeof useDigitalGeniusChatDataStore>;

const DigitalGeniusChatContext = createContext<UseDigitalGeniusChatDataStoreReturnType | null>(null);

export function DigitalGeniusChatContextProvider({ children }: { children: ReactNode }) {
	const digitalGeniusChatData = useDigitalGeniusChatDataStore();
	return (
		<DigitalGeniusChatContext.Provider value={digitalGeniusChatData}>
			{children}
		</DigitalGeniusChatContext.Provider>
	);
}

// SelectorOutput will catch the type of the selected value
// selector will rerender only components that register it
export function useDigitalGeniusChatData<SelectorOutput>(
	selector: (user: DigitalGeniusChatDataType) => SelectorOutput 
): [ SelectorOutput, (value: Partial<DigitalGeniusChatDataType>) => void] {
	const digitalGeniusChatContext = useContext(DigitalGeniusChatContext);
	if (!digitalGeniusChatContext) {
		throw new Error("DigitalGeniusChatContext not found!");
	}
	const state = useSyncExternalStore(digitalGeniusChatContext.subscribe, () => selector(digitalGeniusChatContext.get()));
	return [state, digitalGeniusChatContext.set]
}

//---------------</Digital genius chat data context>--------------------------------------------------------------------
//---------------<Data load functions>----------------------------------------------------------------------------------

export async function getClientDetailsForDG(rootApiEndpointUrl: string ,userID: number, authToken: string, abortControllerSignal?: AbortSignal | null | undefined): Promise<Partial<DigitalGeniusChatDataCallReturnType>> {
	let getClientDetailsForDgData: Partial<DigitalGeniusChatDataCallReturnType> = {};

	const formData = new FormData();
	formData.append("token", authToken);
	formData.append("pageParams[customerID]", String(userID));

	const getClientDetailsForDGCallResult = await apiPOST(`${rootApiEndpointUrl}${import.meta.env.REACT_APP_API_GET_CLIENT_DETAILS_FOR_DG}`, formData, abortControllerSignal);
	
	if (typeof getClientDetailsForDGCallResult === "object" && getClientDetailsForDGCallResult !== null) {
		getClientDetailsForDgData = getClientDetailsForDGCallResult as DigitalGeniusChatDataCallReturnType;
	}
	else if (typeof getClientDetailsForDGCallResult === "string") {
		getClientDetailsForDgData = { errors: ["Client details for DG data error: " + getClientDetailsForDGCallResult] } as DigitalGeniusChatDataCallReturnType;
	}
	/*
	this will happen when we abort a fetch because we set it to return null
	else {
		const error = "Client details for DG failed without errors!";
		getClientDetailsForDgData = { errors: [error] };
	}
	*/

	return getClientDetailsForDgData;
}

//---------------</Data load functions>--------------------------------------------------------------------------------
